/**
 * 角色管理页面
 */
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Table } from "antd";
import { ExclamationCircleOutlined, UserOutlined } from "@ant-design/icons";

// 通用组件
import { GenericCardsStatistic } from "@ms/shared-components";
import ContainerPanel from "@ms/shared-components/containers/ContainerPanel";
import ContainerModal from "@ms/shared-components/containers/ContainerModal";
import CompsSearch from "@ms/shared-components/comps/CompsSearch";
import CompsTable from "@ms/shared-components/comps/CompsTable";
import CompsForm from "@ms/shared-components/comps/CompsForm";
import CompsTree from "@ms/shared-components/comps/CompsTree";

// 角色操作相关hooks
import { useRoleStats, useRoleList, useRoleCRUD, useRoleSelection, useRoleModals } from "./hooks/useRoleOperations";

// 管理员操作相关hooks
import { useAdminSelectModal, useAdminModals } from "./hooks/useAdminOperations";

// 权限操作相关hooks
import { useRolePermissionTreeData, usePermissionViewModal } from "./hooks/usePermissionOperations";

// 配置
import {
  Role,
  createRoleColumns,
  createTableButtonsConfig,
  getTableHeaderButtons,
  tablePanelConfig,
} from "./config/panelTableConfig";
import { roleModalConfig } from "./config/modalFormConfig";
import { createStatisticConfig, statisticLayoutConfig } from "./config/statisticConfig";
import {
  createSearchConfig,
  searchPanelConfig,
  getSearchPanelButtons,
  getSearchProps,
} from "./config/panelSearchConfig";
// 模态配置使用各自文件导出的常量，与容器组合时在页面内组装
import {
  permissionViewModalSize,
  permissionViewSearchConfig,
  permissionViewSelectionConfig,
  permissionViewTreeConfig,
  permissionViewModalProps,
  permissionViewActionConfig,
} from "./config/modalPermissionConfig";
import {
  adminSelectColumns,
  Admin as SelectAdmin,
  adminSelectModalConfig,
  getAdminSelectFooterButtons,
} from "./config/modalAdminConfig";

// 服务
import { RoleManagementService } from "./services/roleManagementService";
import { RolePermissionService } from "./services/rolePermissionService";

const RolesPage: React.FC = () => {
  const { confirm } = Modal;

  // 使用角色操作相关hooks
  const {
    createModalVisible,
    editModalVisible,
    currentRecord,
    showCreateModal,
    showEditModal,
    hideCreateModal,
    hideEditModal,
    setCurrentRecord,
  } = useRoleModals();

  // 使用管理员操作相关hooks
  const { assignAdminModalVisible, showAssignAdminModal, hideAssignAdminModal } = useAdminModals();

  // 使用权限操作相关hooks
  const {
    permissionViewVisible,
    currentRecord: permissionCurrentRecord,
    permissions,
    permissionLoading,
    showPermissionModal,
    hidePermissionModal,
  } = usePermissionViewModal();

  // 使用自定义hooks
  const {
    roleList,
    setRoleList,
    loading,
    pagination,
    setPagination,
    total,
    handleSearch,
    handleReset,
    handlePaginationChange,
  } = useRoleList();

  const { roleStats, loadRoleStats } = useRoleStats(roleList);
  const { selectedRowKeys, selectedRows, handleRowSelectionChange, clearSelection } = useRoleSelection();

  const { handleCreate, handleEdit, handleDelete, handleBatchDelete, handleToggleStatus } = useRoleCRUD(() => {
    handleSearch();
    loadRoleStats();
  });

  // 初始化加载数据
  useEffect(() => {
    handleSearch();
  }, []);

  // 更新统计信息
  useEffect(() => {
    loadRoleStats();
  }, [roleList]);

  // 删除角色确认
  const handleDeleteConfirm = async (record: Role) => {
    confirm({
      title: "确认删除",
      icon: <ExclamationCircleOutlined />,
      content: `确定要删除角色"${record.name}"吗？此操作不可撤销。`,
      okText: "确定",
      cancelText: "取消",
      onOk: async () => {
        await handleDelete(record, () => {
          handleSearch();
          loadRoleStats();
        });
      },
    });
  };

  // 批量删除角色确认
  const handleBatchDeleteConfirm = () => {
    // console.log("批量删除被调用", { selectedRowKeys, selectedRows });
    if (selectedRows.length === 0) {
      return;
    }

    confirm({
      title: "确认批量删除",
      icon: <ExclamationCircleOutlined />,
      content: `确定要删除选中的 ${selectedRows.length} 个角色吗？此操作不可撤销。`,
      okText: "确定",
      cancelText: "取消",
      onOk: async () => {
        await handleBatchDelete(selectedRowKeys, () => {
          handleSearch();
          loadRoleStats();
        });
        clearSelection();
      },
    });
  };

  // 分配管理员（点击管理员数量直接打开选择对话框-多选并可保存）
  const handleAssignAdmins = (record: Role) => {
    // console.debug("[RolesPage] 点击管理员数量，准备打开选择弹窗", { record });
    setCurrentRecord(record);
    // 等待 state 更新后再打开弹窗，避免 roleId 为空导致数据不加载
    setTimeout(() => {
      // console.debug("[RolesPage] 打开管理员选择弹窗");
      adminSelectModal.openModal({
        ...adminSelectModalConfig,
        title: `分配管理员 - ${record.name}`,
        selectionType: "checkbox", // 使用复选框
        multiple: true, // 支持多选
        confirmText: "保存",
      });
    }, 0);
  };

  // 查看权限
  const handleViewPermissions = (record: Role) => {
    showPermissionModal(record);
  };

  // 权限查看弹窗关闭处理
  const handlePermissionViewClose = () => {
    hidePermissionModal();
  };

  // 使用权限树形数据hook
  const { permissionTreeData } = useRolePermissionTreeData({
    permissions,
    currentRecord: permissionCurrentRecord,
  });

  // 表格操作配置
  const columns = createRoleColumns(
    (record) => {
      showEditModal(record);
    },
    (record) => {
      handleToggleStatus(record, () => {
        handleSearch();
        loadRoleStats();
      });
    },
    handleAssignAdmins,
    handleDeleteConfirm,
    handleViewPermissions
  );

  // ================= 管理员选择对话框 =================
  const adminSelectModal = useAdminSelectModal({
    roleId: currentRecord?.id || "",
    visible: assignAdminModalVisible,
    onSuccess: () => {
      handleSearch();
      loadRoleStats();
    },
  });

  // 配置对象
  const searchConfig = createSearchConfig();
  const statisticConfig = createStatisticConfig(roleStats);
  const tableButtons = createTableButtonsConfig(
    () => handleSearch(),
    () => showCreateModal(),
    handleBatchDeleteConfirm,
    selectedRows.length
  );

  return (
    <div
      className="role-management-container"
      style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, gap: 16 }}
    >
      {/* 第一行：统计信息 */}
      <GenericCardsStatistic
        data={statisticConfig.cards}
        columns={statisticLayoutConfig.columns}
        gutter={statisticLayoutConfig.gutter}
      />

      {/* 第二行：搜索栏 */}
      <ContainerPanel
        title={searchPanelConfig.title}
        height="auto"
        buttons={getSearchPanelButtons(handleReset, handleSearch)}
      >
        <CompsSearch {...getSearchProps(searchConfig.fields, handleSearch, handleReset)} />
      </ContainerPanel>

      {/* 第三行：表格 */}
      <ContainerPanel
        title={tablePanelConfig.title}
        buttons={getTableHeaderButtons(tableButtons)}
        height="100%"
        cardProps={{ style: { flex: 1, minHeight: 0 } }}
      >
        <CompsTable
          tableProps={{
            columns: columns as any,
            dataSource: roleList,
            rowKey: "id",
            scroll: { x: 1200, y: "100%" },
            rowSelection: {
              selectedRowKeys,
              onChange: handleRowSelectionChange,
              getCheckboxProps: (record: Role) => ({ name: record.name }),
            },
          }}
          loading={loading}
          showSelection={true}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (totalNum: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条/共 ${totalNum} 条`,
          }}
          onPaginationChange={(p: any) => {
            setPagination({ current: p.current, pageSize: p.pageSize });
            handleSearch({ current: p.current, pageSize: p.pageSize });
          }}
          showColumnSetting={true}
          storageKey={tablePanelConfig.storageKey}
          resizableColumns={tablePanelConfig.resizableColumns}
          saveColumnWidths={tablePanelConfig.saveColumnWidths}
        />
      </ContainerPanel>

      {/* 创建角色弹窗 */}
      {(() => {
        const createFormRef = useRef<any>(null);
        return (
          <ContainerModal
            title="创建角色"
            visible={createModalVisible}
            width={roleModalConfig.modalProps.width}
            height={500}
            onCancel={hideCreateModal}
            footerRightButtons={[
              {
                key: "cancel",
                text: roleModalConfig.modalProps.cancelText,
                type: "default",
                onClick: hideCreateModal,
              },
              {
                key: "ok",
                text: roleModalConfig.modalProps.okText,
                type: "primary",
                onClick: () => createFormRef.current?.submit(),
              },
            ]}
            modalProps={{
              destroyOnClose: roleModalConfig.modalProps.destroyOnClose,
            }}
          >
            <CompsForm
              formItems={roleModalConfig.createFormConfig()}
              formRef={(form) => (createFormRef.current = form)}
              formProps={{
                onFinish: async (values) => {
                  const ok = await handleCreate(values);
                  if (ok) {
                    hideCreateModal();
                  }
                },
              }}
              layout={roleModalConfig.modalProps.layout}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 16 }}
            />
          </ContainerModal>
        );
      })()}

      {/* 编辑角色弹窗 */}
      {(() => {
        const editFormRef = useRef<any>(null);
        return (
          <ContainerModal
            title="编辑角色"
            visible={editModalVisible}
            width={roleModalConfig.modalProps.width}
            height={500}
            onCancel={hideEditModal}
            footerRightButtons={[
              {
                key: "cancel",
                text: roleModalConfig.modalProps.cancelText,
                type: "default",
                onClick: hideEditModal,
              },
              {
                key: "ok",
                text: roleModalConfig.modalProps.okText,
                type: "primary",
                onClick: () => editFormRef.current?.submit(),
              },
            ]}
            modalProps={{
              destroyOnClose: roleModalConfig.modalProps.destroyOnClose,
            }}
          >
            <CompsForm
              formItems={roleModalConfig.editFormConfig()}
              initialValues={currentRecord || undefined}
              formRef={(form) => (editFormRef.current = form)}
              formProps={{
                onFinish: async (values) => {
                  const ok = await handleEdit(values, currentRecord);
                  if (ok) {
                    hideEditModal();
                  }
                },
              }}
              layout={roleModalConfig.modalProps.layout}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 16 }}
            />
          </ContainerModal>
        );
      })()}

      {/* 管理员选择对话框 */}
      <ContainerModal
        title={adminSelectModal.config.title || adminSelectModalConfig.title}
        visible={adminSelectModal.visible}
        width={adminSelectModal.config.width || adminSelectModalConfig.width}
        onCancel={adminSelectModal.handleClose}
        footerRightButtons={getAdminSelectFooterButtons({
          onCancel: adminSelectModal.handleClose,
          onOk: () => adminSelectModal.handleConfirm(),
          loading: adminSelectModal.loading,
        })}
        modalProps={{
          destroyOnClose: true,
          styles: { body: { height: adminSelectModalConfig.fixedHeight, padding: "32px 0" } },
        }}
      >
        <CompsTable<SelectAdmin>
          tableProps={{
            columns: adminSelectColumns,
            dataSource: adminSelectModal.availableAdmins,
            rowKey: "id",
            scroll: { x: 400 },
            size: "small",
            bordered: true,
            rowSelection: {
              type: "checkbox",
              selectedRowKeys: adminSelectModal.selectedKeys,
              onChange: (keys, rows): void => {
                adminSelectModal.handleSelectionChange(keys as any, rows as any);
              },
            },
          }}
          loading={adminSelectModal.loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          showSelection={true}
        />
      </ContainerModal>

      {/* 权限查看弹窗 */}
      {permissionCurrentRecord && (
        <ContainerModal
          title={`${permissionCurrentRecord.name} 的权限详情`}
          visible={permissionViewVisible}
          width={permissionViewModalSize.width}
          onCancel={handlePermissionViewClose}
          footerRightButtons={[
            {
              key: "close",
              text: permissionViewActionConfig.closeText,
              type: "default",
              onClick: handlePermissionViewClose,
            },
          ]}
          modalProps={{
            ...permissionViewModalProps,
            styles: { body: { height: permissionViewModalSize.height, padding: "32px 0" } },
          }}
        >
          <CompsTree
            treeData={permissionTreeData as any}
            loading={permissionLoading}
            showSearch={permissionViewSearchConfig.enabled}
            searchPlaceholder={permissionViewSearchConfig.placeholder}
            defaultExpandAll={permissionViewTreeConfig.defaultExpandAll}
            showIcon={permissionViewTreeConfig.showIcon}
            height="100%"
          />
        </ContainerModal>
      )}
    </div>
  );
};

export default RolesPage;
